import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Clock, MapPin, Package, TrendingUp, Star, MessageCircle, X, Camera, List, Edit, Upload, Send, BarChart3, Check, Minus, DollarSign, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getCurrencyByCountry } from '@/utils/currency';
import { useFxRates, formatConverted } from '@/utils/fx';
import AdBanner from '@/components/AdBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ListingDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBidModal, setShowBidModal] = useState(false);
  const [showCounterModal, setShowCounterModal] = useState(false);
  const [showEditBidModal, setShowEditBidModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [userListings, setUserListings] = useState([]);
  const [bidItemSource, setBidItemSource] = useState('listings'); // 'listings' or 'camera'
  const [cameraItem, setCameraItem] = useState({ title: '', description: '', images: [] });
  const [bidData, setBidData] = useState({
    items: [],
    money_amount: '',
    message: '',
    delivery_terms: ''
  });
  const [editBidData, setEditBidData] = useState({
    items: [],
    money_amount: '',
    message: '',
    delivery_terms: ''
  });
  const [counterMessage, setCounterMessage] = useState('');
  const [counterAmount, setCounterAmount] = useState('');
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareBids, setCompareBids] = useState([]);

  useEffect(() => {
    fetchListing();
    fetchBids();
    fetchInventory();
    fetchUserListings();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      toast.error('Failed to fetch listing');
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bids/listing/${id}`);
      setBids(response.data);
    } catch (error) {
      console.error('Failed to fetch bids', error);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory`);
      setInventoryItems(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory', error);
    }
  };

  const fetchUserListings = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/api/listings/user/${user.id}`);
      // Filter to show only live listings that are not the current listing
      const availableListings = response.data.filter(l => l.status === 'live' && l.id !== id);
      setUserListings(availableListings);
    } catch (error) {
      console.error('Failed to fetch user listings', error);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCameraItem(prev => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addCameraItemToBid = () => {
    if (!cameraItem.title.trim()) {
      toast.error('Please enter a title for the item');
      return;
    }
    const newItem = {
      id: `camera-${Date.now()}`,
      title: cameraItem.title,
      description: cameraItem.description,
      images: cameraItem.images,
      type: 'camera'
    };
    setBidData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setCameraItem({ title: '', description: '', images: [] });
    toast.success('Item added to bid');
  };

  const handlePlaceBid = async () => {
    try {
      await axios.post(`${API_URL}/api/bids`, {
        listing_id: id,
        ...bidData
      });
      toast.success('Bid placed successfully!');
      setShowBidModal(false);
      fetchBids();
      setBidData({ items: [], money_amount: '', message: '', delivery_terms: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bid');
    }
  };

  const handleEditBid = async () => {
    try {
      // Format items to match backend BidItemCreate schema
      const formattedItems = editBidData.items.map(item => ({
        item_id: item.item_id || item.id || null,
        title: item.title,
        description: item.description || '',
        category: item.category || '',
        condition: item.condition || 'good',
        estimated_value: item.estimated_value || null,
        images: item.images || [],
        type: item.type || null
      }));
      
      await axios.put(`${API_URL}/api/bids/${selectedBid.id}`, {
        items: formattedItems,
        money_amount: editBidData.money_amount || 0,
        message: editBidData.message,
        delivery_terms: editBidData.delivery_terms
      });
      toast.success('Bid updated successfully!');
      setShowEditBidModal(false);
      fetchBids();
      setSelectedBid(null);
      setEditBidData({ items: [], money_amount: '', message: '', delivery_terms: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bid');
    }
  };

  const openEditBidModal = (bid) => {
    setSelectedBid(bid);
    setEditBidData({
      items: bid.items || [],
      money_amount: bid.money_amount || '',
      message: bid.message || '',
      delivery_terms: bid.delivery_terms || ''
    });
    setShowEditBidModal(true);
  };

  const handleAcceptBid = async (bidId) => {
    try {
      await axios.patch(`${API_URL}/api/bids/${bidId}/status?new_status=accepted`);
      await axios.post(`${API_URL}/api/trades`, {
        listing_id: id,
        winning_bid_id: bidId
      });
      toast.success('Bid accepted! Trade created.');
      navigate('/trades');
    } catch (error) {
      toast.error('Failed to accept bid');
    }
  };

  const handleRejectBid = async (bidId) => {
    try {
      await axios.patch(`${API_URL}/api/bids/${bidId}/status?new_status=rejected`);
      toast.success('Bid rejected');
      fetchBids();
    } catch (error) {
      toast.error('Failed to reject bid');
    }
  };

  const handleCounterBid = async () => {
    try {
      let url = `${API_URL}/api/bids/${selectedBid.id}/status?new_status=countered`;
      if (counterMessage) {
        url += `&counter_message=${encodeURIComponent(counterMessage)}`;
      }
      if (counterAmount > 0) {
        url += `&counter_amount=${counterAmount}`;
      }
      await axios.patch(url);
      toast.success('Counter offer sent');
      setShowCounterModal(false);
      setCounterMessage('');
      setCounterAmount(0);
      fetchBids();
    } catch (error) {
      toast.error('Failed to send counter offer');
    }
  };

  const addItemToBid = (item) => {
    setBidData({
      ...bidData,
      items: [
        ...bidData.items,
        {
          item_id: item.id,
          title: item.title,
          description: item.description,
          category: item.category,
          condition: item.condition,
          estimated_value: item.estimated_value,
          images: item.images
        }
      ]
    });
  };

  const removeItemFromBid = (index) => {
    const newItems = bidData.items.filter((_, i) => i !== index);
    setBidData({ ...bidData, items: newItems });
  };

  // Message functions
  const openMessageModal = async (bid) => {
    setSelectedBid(bid);
    setShowMessageModal(true);
    setNewMessage('');
    await fetchMessages(bid.id);
  };

  const fetchMessages = async (bidId) => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/bid/${bidId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedBid) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API_URL}/api/messages`, {
        bid_id: selectedBid.id,
        content: newMessage.trim()
      });
      setNewMessage('');
      await fetchMessages(selectedBid.id);
      toast.success('Message sent!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const isOwner = listing && user && listing.user_id === user.id;
  const hasUserBid = user ? bids.some((bid) => bid.bidder_id === user.id) : false;
  const submittedBids = bids.filter(b => b.status === 'submitted');
  const currency = listing ? getCurrencyByCountry(listing.country) : { symbol: '$' };
  const fxRates = useFxRates();
  const viewerCountry = user?.country;
  const showConverted = (amt) => formatConverted(amt, listing?.country, viewerCountry, fxRates);

  const toggleBidCompare = (bid) => {
    setCompareBids(prev => {
      if (prev.find(b => b.id === bid.id)) {
        return prev.filter(b => b.id !== bid.id);
      }
      if (prev.length >= 4) {
        toast.error('You can compare up to 4 bids at a time');
        return prev;
      }
      return [...prev, bid];
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Listing not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Listing Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
              <div className="aspect-video bg-slate-100 overflow-hidden">
                {listing.images && listing.images.length > 0 ? (
                  <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-32 h-32 text-slate-300" />
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {listing.title}
                    </h1>
                    <div className="flex items-center space-x-4 text-sm text-slate-600">
                      <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">{listing.category}</span>
                      <span className="capitalize">{listing.condition.replace('_', ' ')}</span>
                      {(listing.city || listing.state || listing.country) && (
                        <span className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {[listing.city, listing.state, listing.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-600 mb-1">Status</div>
                    <span className="px-3 py-1 bg-lime-100 text-lime-700 rounded-full font-semibold text-sm capitalize">
                      {listing.status}
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold text-slate-900 mb-2">Description</h3>
                  <p className="text-slate-600 leading-relaxed">{listing.description}</p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-slate-900 mb-3">What the seller wants</h3>
                  {listing.preferences.specific_items && (
                    <p className="text-slate-700 mb-2">{listing.preferences.specific_items}</p>
                  )}
                  {listing.preferences.preferred_categories.length > 0 && (
                    <div className="mb-2">
                      <span className="text-sm text-slate-600">Preferred categories: </span>
                      <span className="text-slate-900">{listing.preferences.preferred_categories.join(', ')}</span>
                    </div>
                  )}
                  {listing.preferences.money_allowed && !listing.preferences.only_barter ? (
                    <div className="text-sm text-slate-600">
                      <span className="text-lime-600 font-medium">
                        {listing.preferences.only_money ? 'Money only' : 'Money offers accepted'}
                      </span>
                      {listing.preferences.acceptable_money_range_min && listing.preferences.acceptable_money_range_max && (
                        <span> ({getCurrencyByCountry(listing.country).symbol}{listing.preferences.acceptable_money_range_min} - {getCurrencyByCountry(listing.country).symbol}{listing.preferences.acceptable_money_range_max})</span>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-amber-600 font-medium">Barter only (no money)</div>
                  )}
                </div>

                {!isOwner && listing.status === 'live' && (
                  user ? (
                    <button
                      onClick={() => setShowBidModal(true)}
                      className="w-full rounded-full font-bold px-8 py-4 bg-lime-500 text-slate-900 hover:bg-lime-400 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all border-2 border-black"
                      data-testid="place-bid-button"
                    >
                      Place Your Bid
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/auth')}
                      className="w-full rounded-full font-bold px-8 py-4 bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg transition-all"
                      data-testid="login-to-bid-button"
                    >
                      Login to Place Bid
                    </button>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Bid Board */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Bids</h2>
                <div className="flex items-center space-x-2">
                  {isOwner && submittedBids.length >= 2 && (
                    <button
                      data-testid="compare-bids-btn"
                      onClick={() => {
                        if (compareBids.length < 2) {
                          setCompareBids(submittedBids.slice(0, Math.min(submittedBids.length, 4)));
                        }
                        setShowCompareModal(true);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200 transition-colors"
                    >
                      <BarChart3 className="w-4 h-4" />
                      Compare
                    </button>
                  )}
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                  <span className="text-2xl font-bold text-indigo-600">{bids.length}</span>
                </div>
              </div>

              <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="bid-board">
                {bids.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>No bids yet</p>
                    <p className="text-sm mt-1">Be the first to bid!</p>
                  </div>
                ) : (
                  bids.map((bid) => (
                    <motion.div
                      key={bid.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`bg-slate-900 text-white p-4 rounded-xl border-l-4 ${
                        bid.starred ? 'border-amber-500' : 'border-lime-500'
                      } ${compareBids.find(b => b.id === bid.id) ? 'ring-2 ring-indigo-400' : ''}`}
                      data-testid={`bid-item-${bid.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold">{bid.items.length} item(s)</p>
                          {bid.money_amount > 0 && (
                            <>
                              <p className="text-lime-400 font-bold text-lg">{currency.symbol}{bid.money_amount}</p>
                              {showConverted(bid.money_amount) && (
                                <p className="text-[11px] text-cyan-300/80 mt-0.5" data-testid={`bid-converted-${bid.id}`}>
                                  {showConverted(bid.money_amount)}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isOwner && bid.status === 'submitted' && (
                            <button
                              data-testid={`compare-toggle-${bid.id}`}
                              onClick={() => toggleBidCompare(bid)}
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                compareBids.find(b => b.id === bid.id)
                                  ? 'bg-indigo-500 border-indigo-500'
                                  : 'border-slate-500 hover:border-indigo-400'
                              }`}
                            >
                              {compareBids.find(b => b.id === bid.id) && <Check className="w-3 h-3 text-white" />}
                            </button>
                          )}
                          {bid.starred && <Star className="w-5 h-5 text-amber-500 fill-amber-500" />}
                        </div>
                      </div>
                      {bid.message && <p className="text-sm text-slate-300 mb-2">{bid.message}</p>}
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span className={`px-2 py-1 rounded-full ${
                          bid.status === 'submitted' ? 'bg-blue-900 text-blue-300' :
                          bid.status === 'accepted' ? 'bg-green-900 text-green-300' :
                          bid.status === 'rejected' ? 'bg-red-900 text-red-300' :
                          bid.status === 'countered' ? 'bg-amber-900 text-amber-300' :
                          'bg-slate-800 text-slate-300'
                        }`}>
                          {bid.status}
                        </span>
                        <span>{new Date(bid.created_at).toLocaleDateString()}</span>
                      </div>
                      {bid.counter_message && (
                        <div className="mt-2 p-2 bg-amber-900/30 rounded text-xs text-amber-200">
                          Counter: {bid.counter_message}
                          {bid.counter_amount > 0 && (
                            <span className="font-semibold ml-1">
                              (Suggested: {getCurrencyByCountry(listing.country).symbol}{bid.counter_amount})
                            </span>
                          )}
                        </div>
                      )}

                      {/* Message button - visible to both owner and bidder */}
                      {(isOwner || bid.bidder_id === user?.id) && (
                        <div className="mt-3">
                          <button
                            onClick={() => openMessageModal(bid)}
                            className="w-full flex items-center justify-center gap-2 bg-violet-500 text-white px-3 py-2 rounded-lg font-semibold text-sm hover:bg-violet-600 transition-colors"
                            data-testid={`message-bid-${bid.id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                            Message {isOwner ? 'Bidder' : 'Seller'}
                          </button>
                        </div>
                      )}
                      
                      {/* Edit button for bidder's own submitted bids */}
                      {bid.bidder_id === user?.id && bid.status === 'submitted' && (
                        <div className="mt-3">
                          <button
                            onClick={() => openEditBidModal(bid)}
                            className="w-full flex items-center justify-center gap-2 bg-indigo-500 text-white px-3 py-2 rounded-lg font-semibold text-sm hover:bg-indigo-600 transition-colors"
                            data-testid={`edit-bid-${bid.id}`}
                          >
                            <Edit className="w-4 h-4" />
                            Edit Bid
                          </button>
                        </div>
                      )}
                      
                      {isOwner && bid.status === 'submitted' && (
                        <div className="mt-3 flex space-x-2">
                          <button
                            onClick={() => handleAcceptBid(bid.id)}
                            className="flex-1 bg-lime-500 text-slate-900 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-lime-400 transition-colors"
                            data-testid={`accept-bid-${bid.id}`}
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => {
                              setSelectedBid(bid);
                              setShowCounterModal(true);
                            }}
                            className="flex-1 bg-amber-500 text-slate-900 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-amber-400 transition-colors"
                            data-testid={`counter-bid-${bid.id}`}
                          >
                            Counter
                          </button>
                          <button
                            onClick={() => handleRejectBid(bid.id)}
                            className="flex-1 bg-red-500 text-white px-3 py-2 rounded-lg font-semibold text-sm hover:bg-red-600 transition-colors"
                            data-testid={`reject-bid-${bid.id}`}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))
                )}
              </div>
            </div>

            {/* Sidebar Ad */}
            <AdBanner placement="listing_sidebar" className="mt-4" />
          </div>
        </div>

        {/* Below Listing Details Ad */}
        <AdBanner placement="listing_bottom" className="mt-6 md:mt-8 max-w-7xl mx-auto px-4" />
      </div>

      {/* Place Bid Modal */}
      <Dialog open={showBidModal} onOpenChange={setShowBidModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="place-bid-modal">
          <DialogHeader>
            <DialogTitle>Place Your Bid</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Product Summary */}
            <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl p-4 border border-indigo-100">
              <div className="flex gap-4">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                  {listing.images && listing.images.length > 0 ? (
                    <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 text-lg truncate">{listing.title}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{listing.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">{listing.category}</span>
                    <span className="text-xs text-slate-500">{listing.condition}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Seller's Preferences */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                What the seller wants
              </h4>
              
              {/* Trade Preferences */}
              <div className="space-y-2 text-sm">
                {listing.preferences?.only_barter && (
                  <p className="text-amber-700">• <span className="font-medium">Barter only</span> - No cash offers accepted</p>
                )}
                {listing.preferences?.only_money && (
                  <p className="text-amber-700">• <span className="font-medium">Cash only</span> - No item offers accepted</p>
                )}
                {listing.preferences?.money_allowed && !listing.preferences?.only_money && !listing.preferences?.only_barter && (
                  <p className="text-amber-700">• Accepts <span className="font-medium">items, cash, or combination</span></p>
                )}
                
                {/* Acceptable Money Range */}
                {(listing.preferences?.acceptable_money_range_min > 0 || listing.preferences?.acceptable_money_range_max > 0) && (
                  <p className="text-amber-700">
                    • Acceptable cash range: <span className="font-bold text-amber-900">
                      {getCurrencyByCountry(listing.country).symbol}{listing.preferences?.acceptable_money_range_min || 0} - {getCurrencyByCountry(listing.country).symbol}{listing.preferences?.acceptable_money_range_max || 'No limit'}
                    </span>
                  </p>
                )}
                
                {/* Preferred Categories */}
                {listing.preferences?.preferred_categories && listing.preferences.preferred_categories.length > 0 && (
                  <div className="text-amber-700">
                    <span>• Preferred categories: </span>
                    <span className="font-medium">{listing.preferences.preferred_categories.join(', ')}</span>
                  </div>
                )}
                
                {/* Specific Items */}
                {listing.preferences?.specific_items && (
                  <p className="text-amber-700">• Looking for: <span className="font-medium">{listing.preferences.specific_items}</span></p>
                )}
              </div>
            </div>

            {/* Item Source Tabs */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Add items to your bid</label>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setBidItemSource('listings')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    bidItemSource === 'listings'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="tab-my-listings"
                >
                  <List className="w-4 h-4" />
                  My Listings
                </button>
                <button
                  type="button"
                  onClick={() => setBidItemSource('camera')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    bidItemSource === 'camera'
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="tab-camera"
                >
                  <Camera className="w-4 h-4" />
                  Take Photo
                </button>
              </div>

              {/* My Listings Tab Content */}
              {bidItemSource === 'listings' && (
                <>
                  {userListings.length === 0 ? (
                    <div className="text-center py-6 bg-slate-50 rounded-xl">
                      <List className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-600 text-sm">No active listings to offer</p>
                      <button
                        onClick={() => navigate('/create-listing')}
                        className="mt-2 text-indigo-600 font-medium hover:text-indigo-700 text-sm"
                      >
                        Create a listing
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 max-h-40 overflow-y-auto">
                      {userListings.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => addItemToBid({ id: item.id, title: item.title, category: item.category, images: item.images, type: 'listing' })}
                          className="border border-slate-200 rounded-lg p-2 hover:border-indigo-500 cursor-pointer transition-colors flex gap-2"
                          data-testid={`listing-item-${item.id}`}
                        >
                          <div className="w-12 h-12 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                            {item.images && item.images.length > 0 ? (
                              <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" />
                            ) : (
                              <Package className="w-6 h-6 text-slate-300 m-3" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{item.title}</p>
                            <p className="text-xs text-slate-500">{item.category}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Camera Tab Content */}
              {bidItemSource === 'camera' && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Item Title *</label>
                    <input
                      type="text"
                      value={cameraItem.title}
                      onChange={(e) => setCameraItem(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-green-500 h-10 px-3 text-sm"
                      placeholder="What are you offering?"
                      data-testid="camera-item-title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                    <input
                      type="text"
                      value={cameraItem.description}
                      onChange={(e) => setCameraItem(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-green-500 h-10 px-3 text-sm"
                      placeholder="Brief description"
                      data-testid="camera-item-description"
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleCameraCapture}
                      className="hidden"
                      id="camera-bid-capture"
                      data-testid="camera-bid-input"
                    />
                    <label
                      htmlFor="camera-bid-capture"
                      className="flex-1 flex items-center justify-center gap-2 cursor-pointer bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
                    >
                      <Camera className="w-4 h-4" />
                      {cameraItem.images.length > 0 ? `${cameraItem.images.length} photo(s)` : 'Take Photo'}
                    </label>
                    <button
                      type="button"
                      onClick={addCameraItemToBid}
                      disabled={!cameraItem.title.trim()}
                      className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
                      data-testid="add-camera-item"
                    >
                      Add to Bid
                    </button>
                  </div>
                  {cameraItem.images.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {cameraItem.images.map((img, idx) => (
                        <div key={img || idx} className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                          <img src={img} alt={`Capture ${idx + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {bidData.items.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Selected items ({bidData.items.length})</label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {bidData.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between bg-indigo-50 p-2 rounded-lg">
                      <div className="flex items-center gap-2">
                        {item.images && item.images.length > 0 && (
                          <div className="w-8 h-8 rounded overflow-hidden bg-slate-100">
                            <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <span className="font-medium text-sm">{item.title}</span>
                        {item.type === 'camera' && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Photo</span>}
                        {item.type === 'listing' && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Listing</span>}
                      </div>
                      <button
                        onClick={() => removeItemFromBid(index)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`remove-item-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Money amount {listing.preferences?.only_barter ? '(Not accepted)' : '(optional)'}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                  {getCurrencyByCountry(listing.country).symbol}
                </span>
                <input
                  type="number"
                  min="0"
                  value={bidData.money_amount}
                  onChange={(e) => setBidData({ ...bidData, money_amount: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 pl-10 pr-4"
                  placeholder="Enter amount"
                  disabled={listing.preferences?.only_barter}
                  data-testid="cash-amount-input"
                />
              </div>
              {(listing.preferences?.acceptable_money_range_min > 0 || listing.preferences?.acceptable_money_range_max > 0) && (
                <p className="text-xs text-slate-500 mt-1">
                  Seller's acceptable range: {getCurrencyByCountry(listing.country).symbol}{listing.preferences?.acceptable_money_range_min || 0} - {getCurrencyByCountry(listing.country).symbol}{listing.preferences?.acceptable_money_range_max || '∞'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Message (optional)</label>
              <textarea
                value={bidData.message}
                onChange={(e) => setBidData({ ...bidData, message: e.target.value })}
                className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 p-4"
                rows="3"
                placeholder="Add a message to the seller..."
                data-testid="bid-message-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Delivery terms (optional)</label>
              <input
                type="text"
                value={bidData.delivery_terms}
                onChange={(e) => setBidData({ ...bidData, delivery_terms: e.target.value })}
                className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                placeholder="Pickup, shipping, etc."
                data-testid="delivery-terms-input"
              />
            </div>

            <Button
              onClick={handlePlaceBid}
              className="w-full rounded-full font-bold px-8 py-3 bg-indigo-600 text-white hover:bg-indigo-700"
              data-testid="submit-bid-button"
            >
              Submit Bid
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Counter Offer Modal */}
      <Dialog open={showCounterModal} onOpenChange={setShowCounterModal}>
        <DialogContent data-testid="counter-offer-modal">
          <DialogHeader>
            <DialogTitle>Counter Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Bid Summary */}
            {selectedBid && (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2 text-sm">Current Bid</h4>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Items offered:</span>
                  <span className="font-medium">{selectedBid.items?.length || 0} item(s)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Cash offered:</span>
                  <span className="font-medium text-lime-600">{getCurrencyByCountry(listing?.country).symbol}{selectedBid.money_amount || 0}</span>
                </div>
                {selectedBid.message && (
                  <div className="mt-2 text-sm">
                    <span className="text-slate-600">Message: </span>
                    <span className="text-slate-700 italic">"{selectedBid.message}"</span>
                  </div>
                )}
              </div>
            )}

            {/* Counter Amount */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Suggest a different amount (optional)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                  {getCurrencyByCountry(listing?.country).symbol}
                </span>
                <input
                  type="number"
                  min="0"
                  value={counterAmount}
                  onChange={(e) => setCounterAmount(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-amber-500 h-12 pl-10 pr-4"
                  placeholder="Enter amount"
                  data-testid="counter-amount-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Leave empty to not suggest a specific amount</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Counter message</label>
              <textarea
                value={counterMessage}
                onChange={(e) => setCounterMessage(e.target.value)}
                className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-amber-500 p-4"
                rows="4"
                placeholder="Explain what changes you want to the bid..."
                data-testid="counter-message-input"
              />
            </div>
            <Button
              onClick={handleCounterBid}
              className="w-full rounded-full font-bold px-8 py-3 bg-amber-500 text-slate-900 hover:bg-amber-400"
              data-testid="submit-counter-button"
            >
              Send Counter Offer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Bid Modal */}
      <Dialog open={showEditBidModal} onOpenChange={setShowEditBidModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="edit-bid-modal">
          <DialogHeader>
            <DialogTitle>Edit Your Bid</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Selected Items */}
            {editBidData.items.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Current items ({editBidData.items.length})</label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {editBidData.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between bg-indigo-50 p-2 rounded-lg">
                      <span className="font-medium text-sm">{item.title}</span>
                      <button
                        onClick={() => setEditBidData(prev => ({
                          ...prev,
                          items: prev.items.filter((_, i) => i !== index)
                        }))}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add More Items */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Add items from your listings</label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                {userListings.filter(l => !editBidData.items.some(i => i.id === l.id)).map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setEditBidData(prev => ({
                      ...prev,
                      items: [...prev.items, { id: item.id, title: item.title, category: item.category }]
                    }))}
                    className="border border-slate-200 rounded-lg p-2 hover:border-indigo-500 cursor-pointer transition-colors"
                  >
                    <p className="font-medium text-sm truncate">{item.title}</p>
                    <p className="text-xs text-slate-500">{item.category}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Money Amount */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Money amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                  {getCurrencyByCountry(listing?.country).symbol}
                </span>
                <input
                  type="number"
                  min="0"
                  value={editBidData.money_amount}
                  onChange={(e) => setEditBidData({ ...editBidData, money_amount: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 pl-10 pr-4"
                  placeholder="Enter amount"
                  data-testid="edit-cash-amount-input"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
              <textarea
                value={editBidData.message}
                onChange={(e) => setEditBidData({ ...editBidData, message: e.target.value })}
                className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 p-4"
                rows="3"
                placeholder="Update your message..."
                data-testid="edit-message-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowEditBidModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditBid}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                data-testid="update-bid-button"
              >
                Update Bid
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col" data-testid="message-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-violet-600" />
              Chat with {isOwner ? 'Bidder' : 'Seller'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col mt-4">
            {/* Chat Info */}
            <div className="bg-slate-50 rounded-xl p-3 mb-4 text-sm">
              <p className="text-slate-600">
                Share pickup address, phone number, or any details about the trade.
              </p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-[300px] min-h-[200px]">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        msg.sender_id === user?.id
                          ? 'bg-indigo-600 text-white rounded-br-md'
                          : 'bg-slate-200 text-slate-900 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_id === user?.id ? 'text-indigo-200' : 'text-slate-500'
                      }`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            <div className="flex gap-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                className="flex-1 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 p-3 resize-none"
                rows="2"
                placeholder="Type your message... (Enter to send)"
                data-testid="message-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!newMessage.trim() || sendingMessage}
                className="bg-indigo-600 hover:bg-indigo-700 px-4"
                data-testid="send-message-btn"
              >
                {sendingMessage ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bid Comparison Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="compare-bids-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              Compare Bids ({compareBids.length})
            </DialogTitle>
          </DialogHeader>

          {compareBids.length < 2 ? (
            <div className="text-center py-8 text-slate-500">
              <p className="text-lg font-medium">Select at least 2 bids to compare</p>
              <p className="text-sm mt-1">Use the checkboxes on bid cards to select bids for comparison</p>
            </div>
          ) : (
            <div className="mt-4">
              {/* Comparison Grid */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" data-testid="compare-table">
                  <thead>
                    <tr>
                      <th className="text-left p-3 bg-slate-50 rounded-tl-lg text-sm font-semibold text-slate-600 w-36 sticky left-0">Criteria</th>
                      {compareBids.map((bid, i) => (
                        <th key={bid.id} className={`p-3 bg-slate-50 text-center min-w-[180px] ${i === compareBids.length - 1 ? 'rounded-tr-lg' : ''}`}>
                          <div className="flex flex-col items-center gap-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              i === 0 ? 'bg-indigo-100 text-indigo-700' :
                              i === 1 ? 'bg-emerald-100 text-emerald-700' :
                              i === 2 ? 'bg-amber-100 text-amber-700' :
                              'bg-rose-100 text-rose-700'
                            }`}>
                              Bid {i + 1}
                            </span>
                            <button
                              onClick={() => toggleBidCompare(bid)}
                              className="text-xs text-slate-400 hover:text-red-500"
                              data-testid={`remove-compare-${bid.id}`}
                            >
                              Remove
                            </button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {/* Status Row */}
                    <tr>
                      <td className="p-3 text-sm font-medium text-slate-600 sticky left-0 bg-white">Status</td>
                      {compareBids.map(bid => (
                        <td key={bid.id} className="p-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            bid.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                            bid.status === 'accepted' ? 'bg-green-100 text-green-700' :
                            bid.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            bid.status === 'countered' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {bid.status}
                          </span>
                        </td>
                      ))}
                    </tr>

                    {/* Cash Amount Row */}
                    <tr className="bg-slate-50/50">
                      <td className="p-3 text-sm font-medium text-slate-600 sticky left-0 bg-slate-50/50">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-3.5 h-3.5" /> Cash Offer
                        </div>
                      </td>
                      {compareBids.map(bid => {
                        const maxCash = Math.max(...compareBids.map(b => b.money_amount || 0));
                        const isBest = bid.money_amount > 0 && bid.money_amount === maxCash;
                        return (
                          <td key={bid.id} className="p-3 text-center">
                            {bid.money_amount > 0 ? (
                              <span className={`text-lg font-bold ${isBest ? 'text-green-600' : 'text-slate-800'}`}>
                                {currency.symbol}{bid.money_amount}
                                {isBest && <span className="ml-1 text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full">Best</span>}
                              </span>
                            ) : (
                              <span className="text-slate-400 flex items-center justify-center gap-1"><Minus className="w-4 h-4" /> No cash</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Items Count Row */}
                    <tr>
                      <td className="p-3 text-sm font-medium text-slate-600 sticky left-0 bg-white">
                        <div className="flex items-center gap-1">
                          <Package className="w-3.5 h-3.5" /> Items Offered
                        </div>
                      </td>
                      {compareBids.map(bid => {
                        const maxItems = Math.max(...compareBids.map(b => (b.items || []).length));
                        const isBest = (bid.items || []).length > 0 && (bid.items || []).length === maxItems;
                        return (
                          <td key={bid.id} className="p-3 text-center">
                            <span className={`text-lg font-bold ${isBest ? 'text-indigo-600' : 'text-slate-800'}`}>
                              {(bid.items || []).length}
                              {isBest && (bid.items || []).length > 0 && <span className="ml-1 text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">Most</span>}
                            </span>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Items Detail Row */}
                    <tr className="bg-slate-50/50">
                      <td className="p-3 text-sm font-medium text-slate-600 sticky left-0 bg-slate-50/50">Item Details</td>
                      {compareBids.map(bid => (
                        <td key={bid.id} className="p-3">
                          {(bid.items || []).length > 0 ? (
                            <div className="space-y-2">
                              {bid.items.map((item, i) => (
                                <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-slate-100">
                                  {item.images && item.images[0] ? (
                                    <img src={item.images[0]} alt={item.title} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                  ) : (
                                    <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                                      <Package className="w-4 h-4 text-slate-300" />
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <p className="text-xs font-semibold text-slate-800 truncate">{item.title}</p>
                                    {item.estimated_value && (
                                      <p className="text-[10px] text-green-600">{currency.symbol}{item.estimated_value}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">No items</span>
                          )}
                        </td>
                      ))}
                    </tr>

                    {/* Estimated Total Value */}
                    <tr>
                      <td className="p-3 text-sm font-medium text-slate-600 sticky left-0 bg-white">Est. Total Value</td>
                      {compareBids.map(bid => {
                        const itemsValue = (bid.items || []).reduce((sum, item) => sum + (item.estimated_value || 0), 0);
                        const total = (bid.money_amount || 0) + itemsValue;
                        const maxTotal = Math.max(...compareBids.map(b => {
                          const iv = (b.items || []).reduce((s, item) => s + (item.estimated_value || 0), 0);
                          return (b.money_amount || 0) + iv;
                        }));
                        const isBest = total > 0 && total === maxTotal;
                        return (
                          <td key={bid.id} className="p-3 text-center">
                            <span className={`text-lg font-bold ${isBest ? 'text-green-600' : 'text-slate-800'}`}>
                              {total > 0 ? `${currency.symbol}${total.toLocaleString()}` : '-'}
                              {isBest && total > 0 && <span className="ml-1 text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full">Highest</span>}
                            </span>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Message Row */}
                    <tr className="bg-slate-50/50">
                      <td className="p-3 text-sm font-medium text-slate-600 sticky left-0 bg-slate-50/50">Message</td>
                      {compareBids.map(bid => (
                        <td key={bid.id} className="p-3">
                          <p className="text-xs text-slate-600 line-clamp-3">{bid.message || <span className="text-slate-400 italic">No message</span>}</p>
                        </td>
                      ))}
                    </tr>

                    {/* Date Row */}
                    <tr>
                      <td className="p-3 text-sm font-medium text-slate-600 sticky left-0 bg-white">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" /> Submitted
                        </div>
                      </td>
                      {compareBids.map(bid => (
                        <td key={bid.id} className="p-3 text-center text-xs text-slate-600">
                          {new Date(bid.created_at).toLocaleDateString()} {new Date(bid.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                      ))}
                    </tr>

                    {/* Actions Row */}
                    <tr className="bg-slate-50/50">
                      <td className="p-3 text-sm font-medium text-slate-600 sticky left-0 bg-slate-50/50">Actions</td>
                      {compareBids.map(bid => (
                        <td key={bid.id} className="p-3">
                          {bid.status === 'submitted' && (
                            <div className="flex flex-col gap-1.5">
                              <button
                                onClick={() => { handleAcceptBid(bid.id); setShowCompareModal(false); }}
                                className="w-full flex items-center justify-center gap-1 bg-lime-500 text-slate-900 px-3 py-1.5 rounded-lg font-semibold text-xs hover:bg-lime-400 transition-colors"
                                data-testid={`compare-accept-${bid.id}`}
                              >
                                <Check className="w-3.5 h-3.5" /> Accept
                              </button>
                              <button
                                onClick={() => { setSelectedBid(bid); setShowCompareModal(false); setShowCounterModal(true); }}
                                className="w-full flex items-center justify-center gap-1 bg-amber-500 text-slate-900 px-3 py-1.5 rounded-lg font-semibold text-xs hover:bg-amber-400 transition-colors"
                                data-testid={`compare-counter-${bid.id}`}
                              >
                                <ArrowRight className="w-3.5 h-3.5" /> Counter
                              </button>
                              <button
                                onClick={() => { handleRejectBid(bid.id); setShowCompareModal(false); }}
                                className="w-full flex items-center justify-center gap-1 bg-red-500 text-white px-3 py-1.5 rounded-lg font-semibold text-xs hover:bg-red-600 transition-colors"
                                data-testid={`compare-reject-${bid.id}`}
                              >
                                <X className="w-3.5 h-3.5" /> Reject
                              </button>
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
